import React from "react";
import { useHistory } from "react-router-dom";
import { useIsEmbedded } from "../../common/embed";
import { Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { SchoolDashboard } from "../../common/api";
import { LessonImage } from "../../common/LessonImage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlaneTop } from "@fortawesome/pro-regular-svg-icons";
import { LessonExecutionStatusBadge } from "./LessonExecutionStatusBadge";
import {
  DashboardCtaButton,
  DataTable,
  LabelWithTooltip,
} from "./DashboardStyles";
import styled from "styled-components";
import {
  COLOR_GREEN,
  COLOR_GREEN_LIGHT,
  COLOR_GRAY_DD,
  COLOR_GRAY_EE,
  COLOR_RED,
  COLOR_WHITE,
} from "../../common/colors";

export const ProgressContainer = styled.div<{ improvement: boolean }>`
  display: flex;
  align-items: center;
  box-sizing: content-box;
  margin-top: 0.14em;
  width: 120px;

  .progress {
    position: relative;
    flex: 1;
    height: 20px;
    background-color: ${COLOR_WHITE};
    border: 1.5px solid ${COLOR_GRAY_DD};
    border-radius: 10px;
    overflow: visible;

    // Inner clipping container for fills
    .progress-clip {
      position: relative;
      height: 100%;
      width: 100%;
      border-radius: inherit;
      overflow: hidden;
    }

    .progress-fill {
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;

      &.prev {
        background-color: ${COLOR_GREEN_LIGHT};
        z-index: 2;
      }
    }

    .marker {
      position: absolute;
      top: -8px;
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      transform: translateX(-50%);
      z-index: 2;

      &.prev {
        border-top: 6px solid ${COLOR_GREEN_LIGHT};
      }
    }
  }

  ${(p) =>
    p.improvement
      ? `
    .progress-fill.current {
      background-color: ${COLOR_GREEN};
    }
    .marker.current {
      border-top: 6px solid ${COLOR_GREEN};
    }
    `
      : `
    .progress-fill.current {
      background-color: ${COLOR_RED};
      z-index: 3;
    }
    .marker.current {
      border-top: 6px solid ${COLOR_RED};
    }
    `}
`;

const PostAssessmentProficiency = ({
  pre,
  current,
}: {
  pre: number | null;
  current: number;
}) => {
  const { t } = useTranslation();
  const isWorse = pre !== null && current < pre;
  const tooltipKey =
    pre === null
      ? "dashboard_assessment_post_assessment_without_pre_assessment"
      : isWorse
        ? "dashboard_assessment_post_assessment_worse_than_pre_assessment"
        : null;
  const text = (
    <>
      {pre !== null && <>{pre}% → </>}
      {current}%
    </>
  );
  return (
    <td style={{ color: isWorse ? COLOR_RED : undefined }}>
      {tooltipKey ? (
        <LabelWithTooltip tooltip={t(tooltipKey)}>{text}</LabelWithTooltip>
      ) : (
        text
      )}
    </td>
  );
};

export const LessonAnalyticsTab = ({
  dashboard,
  navigate,
}: {
  dashboard: SchoolDashboard & { is_at_lesson_level: true };
  navigate: (string) => void;
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const isEmbedded = useIsEmbedded();
  const [userIdsWithExecutions, setUserIdsWithExecutions] = React.useState<
    string[]
  >([]);
  React.useEffect(() => {
    setUserIdsWithExecutions(
      dashboard.lesson_executions.map((execution) => execution.user_id)
    );
  }, [dashboard]);

  return (
    <div>
      <Stack
        className="py-4"
        direction="horizontal"
        gap={3}
        style={{ borderBottom: `1px solid ${COLOR_GRAY_EE}` }}
      >
        <div>
          <LessonImage
            style={{ width: "auto", height: "6em", borderRadius: "5px" }}
            thumbSize="large_cover_ios_2x"
            url={dashboard.cover_asset}
            alt="Lesson cover image"
          />
        </div>
        <Stack>
          <div>
            <b>{dashboard.lesson_title}</b>
          </div>
          {dashboard.show_only_assigned_students ? (
            <div>
              <b>{t("dashboard_column_assigned_completions")}: </b>
              {dashboard.lessons_completed}/{dashboard.students_assigned}
            </div>
          ) : (
            <div>
              <b>{t("dashboard_column_completions")}: </b>
              {dashboard.lessons_completed}
            </div>
          )}
          {!isEmbedded && (
            <DashboardCtaButton
              className="align-self-start mt-2"
              onClick={() => history.push("/my-class/assignments")}
            >
              <FontAwesomeIcon icon={faPaperPlaneTop} />
              {t("dashboard_lesson_assign")}
            </DashboardCtaButton>
          )}
        </Stack>
      </Stack>
      <DataTable className="mt-4">
        <thead>
          <tr>
            <th scope="col">{t("dashboard_column_student_name")}</th>
            <th scope="col" className="text-start">
              {t("dashboard_column_results")}
            </th>
            {(dashboard.is_pre_assessment || dashboard.is_post_assessment) && (
              <>
                <th scope="col">
                  <LabelWithTooltip
                    tooltip={t(
                      "dashboard_assessment_column_lessons_completed_tooltip"
                    )}
                  >
                    {t("dashboard_column_lessons_completed")}
                  </LabelWithTooltip>
                </th>
                <th scope="col" className="text-start" colSpan={2}>
                  <LabelWithTooltip
                    tooltip={t(
                      dashboard.is_pre_assessment
                        ? "dashboard_assessment_pre_assessment_proficiency_tooltip"
                        : "dashboard_assessment_post_assessment_proficiency_tooltip"
                    )}
                  >
                    {t("dashboard_column_proficiency")}
                  </LabelWithTooltip>
                </th>
              </>
            )}
            <th scope="col" className="text-start">
              {t("dashboard_column_next_lesson")}
            </th>
            <th scope="col">{t("dashboard_column_date_completed")}</th>
          </tr>
        </thead>
        <tbody>
          {dashboard.lesson_executions.map((execution, index) => (
            <tr key={"lx-" + index}>
              <td>
                <a
                  href={execution.student_url}
                  onClick={() => navigate(execution.student_url)}
                >
                  {execution.student_name}
                </a>
              </td>
              <td className="text-start">
                <LessonExecutionStatusBadge
                  isComplete={execution.is_complete}
                  isGood={execution.is_good}
                  isBad={execution.is_bad}
                  executionUrl={execution.execution_url}
                  onClick={(url) => navigate(url)}
                />
              </td>
              {(dashboard.is_pre_assessment ||
                dashboard.is_post_assessment) && (
                <td>
                  {execution.completed_before} out of{" "}
                  {dashboard.all_lessons_count}
                </td>
              )}
              {dashboard.is_pre_assessment ? (
                execution.proficiency === null ? (
                  <td className="text-start" colSpan={2}>
                    -
                  </td>
                ) : (
                  <>
                    <td className="text-start">
                      <ProgressContainer improvement={true}>
                        <div className="progress">
                          <div className="progress-clip">
                            <div
                              className="progress-fill prev"
                              style={{ width: `${execution.proficiency}%` }}
                            ></div>
                          </div>
                          <div
                            className="marker prev"
                            style={{ left: `${execution.proficiency}%` }}
                          ></div>
                        </div>
                      </ProgressContainer>
                    </td>
                    <td>{execution.proficiency}%</td>
                  </>
                )
              ) : dashboard.is_post_assessment ? (
                execution.proficiency === null ? (
                  <td className="text-start" colSpan={2}>
                    -
                  </td>
                ) : (
                  <>
                    <td className="text-start">
                      <ProgressContainer
                        improvement={
                          execution.proficiency_pre === null ||
                          execution.proficiency >= execution.proficiency_pre
                        }
                      >
                        <div className="progress">
                          <div className="progress-clip">
                            {execution.proficiency_pre !== null && (
                              <div
                                className="progress-fill prev"
                                style={{
                                  width: `${execution.proficiency_pre}%`,
                                }}
                              ></div>
                            )}
                            <div
                              className="progress-fill current"
                              style={{ width: `${execution.proficiency}%` }}
                            ></div>
                          </div>
                          {execution.proficiency_pre !== null && (
                            <div
                              className="marker prev"
                              style={{
                                left: `${execution.proficiency_pre}%`,
                              }}
                            ></div>
                          )}
                          <div
                            className="marker current"
                            style={{ left: `${execution.proficiency}%` }}
                          ></div>
                        </div>
                      </ProgressContainer>
                    </td>
                    <PostAssessmentProficiency
                      pre={execution.proficiency_pre}
                      current={execution.proficiency}
                    />
                  </>
                )
              ) : (
                <></>
              )}
              <td className="text-start">
                {execution.first_suggested_lesson_name || "-"}
              </td>
              <td>{execution.completion_date}</td>
            </tr>
          ))}
          {dashboard.user_info
            .filter(
              (u) =>
                u.lessons_completed === 0 &&
                !userIdsWithExecutions.includes(u.user_id)
            )
            .map((user, index) => (
              <tr key={"user-" + index}>
                <td>
                  <a href={user.url} onClick={() => navigate(user.url)}>
                    {user.first_name + " " + user.last_name}
                  </a>
                </td>
                <td className="text-secondary text-start">-</td>
                {(dashboard.is_pre_assessment ||
                  dashboard.is_post_assessment) && (
                  <>
                    <td className="text-secondary">-</td>
                    <td className="text-secondary text-start" colSpan={2}>
                      -
                    </td>
                  </>
                )}
                <td className="text-secondary text-start">-</td>
                <td className="text-secondary">-</td>
              </tr>
            ))}
        </tbody>
      </DataTable>
    </div>
  );
};
